import TranslationTableContext from "./Context/Context";
import { IScreenControllerTranslations } from "@screens/TranslationsScreen/connector/__types__/UIViewController";
import { useControllers } from "@view/hooks/useControllers";
import { Spin, TableProps, Typography } from "antd";
import { observer } from "mobx-react";
import React from "react";
import { EditTwoTone } from "@ant-design/icons";
import { ITranslateEntity } from "@entities/TranslateEntity";
import { Button } from "@shared/controls/Button";
import Table from "@shared/table/Table";

const HighlightText = ({ text, search }: { text: string; search: string }) => {
  if (!search) {
    return <span style={{ overflowWrap: "anywhere" }}>{text}</span>;
  }

  const regex = new RegExp(`(${search})`, "gi");
  const parts = text.split(regex);

  return (
    <span style={{ overflowWrap: "anywhere" }}>
      {parts.map((part, index) =>
        regex.test(part) ? (
          <Typography.Text
            style={{ overflowWrap: "anywhere" }}
            key={index}
            mark
          >
            {part}
          </Typography.Text>
        ) : (
          part
        )
      )}
    </span>
  );
};

const TranslationsTable = observer((): JSX.Element => {
  const { UIViewController } = useControllers<{
    UIViewController: IScreenControllerTranslations;
  }>();

  const columns: TableProps<ITranslateEntity>["columns"] = [
    {
      dataIndex: "id",
      key: "id",
      sorter: (a, b) => a.id - b.id,
      title: "ID",
      width: "5%",
    },
    {
      dataIndex: "hash",
      ellipsis: false,
      key: "key",
      render: (_, record) => (
        <HighlightText
          text={record.key}
          search={UIViewController.filters.search}
        />
      ),
      sorter: (a, b) => a.key.localeCompare(b.key),
      title: "Key",
      width: "100px",
    },

    {
      dataIndex: "en",
      ellipsis: false,
      key: "en",
      render: (_, record) => (
        <HighlightText
          text={record.translations.en}
          search={UIViewController.filters.search}
        />
      ),
      title: "English",
      width: "25%",
    },
    {
      dataIndex: "ar",
      ellipsis: false,
      key: "ar",
      render: (_, record) => (
        <HighlightText
          text={record.translations.ar}
          search={UIViewController.filters.search}
        />
      ),
      title: "Arabic",
      width: "25%",
    },
    {
      dataIndex: "segment",
      ellipsis: false,
      key: "segment",
      render: (_, record) => (
        <HighlightText
          text={record.segment === "messages" ? "backend" : record.segment}
          search={UIViewController.filters.search}
        />
      ),
      title: "Segment",
      width: "5%",
    },
    {
      key: "context",
      render: (_, record) => (
        <TranslationTableContext
          context={record.context || ""}
          search={UIViewController.filters.search}
        />
      ),
      title: "Context",
      width: "5%",
    },
    {
      key: "action",
      render: (_, record) => (
        <Button
          type="link"
          shape="circle"
          size="small"
          icon={<EditTwoTone />}
          onClick={() => UIViewController.openTranslationEditScene(record)}
        >
          Edit
        </Button>
      ),
      title: "Action",
      width: "5%",
    },
  ];

  return (
    <Spin spinning={UIViewController.isRequestInProgress}>
      <Table
        pagination={{
          pageSize: 20,
        }}
        dataSource={UIViewController.listOfFilteredTranslations}
        columns={columns}
        rowKey={(record) => record.hash}
      />
    </Spin>
  );
});

export default TranslationsTable;
